/* eslint-disable no-console */
import {envConfig} from 'config';

enum LogLevel {
    Debug = 'debug',
    Info = 'info',
    Warn = 'warn',
    Error = 'error'
}

const LOG_LEVEL_VALUE = {
    [LogLevel.Debug]: 10,
    [LogLevel.Info]: 20,
    [LogLevel.Warn]: 30,
    [LogLevel.Error]: 40,
};

const LOG_LEVEL_METHOD = {
    [LogLevel.Debug]: 'debug',
    [LogLevel.Info]: 'info',
    [LogLevel.Warn]: 'warn',
    [LogLevel.Error]: 'error',
};

let logLevel = LOG_LEVEL_VALUE[envConfig.logLevel] ? envConfig.logLevel : LogLevel.Warn;

// just one more easter egg to dynamically change log level if needed for debugging
(window as any).__setLogLevel = (level: LogLevel) => {
    if (!LOG_LEVEL_VALUE[level]) {
        console.error('Invalid log level provided');
        return;
    }
    logLevel = level;
};
(window as any).__logTrace = false;

const getDatePrefix = () => `[${new Date().toISOString()}]`;
const canLog = (level: LogLevel) => LOG_LEVEL_VALUE[logLevel] <= LOG_LEVEL_VALUE[level];

enum LogCategory {
    WebRTC = 'WebRTC',
    Twilio = 'Twilio',
    Analytics = 'Analytics',
    PartnerStack = 'PartnerStack',
    Notification = 'Notification',
    NativeInterface = 'NativeInterface'
}

type LogMessageWithCategory = {
    category: LogCategory;
    message: string;
}
type LogMessage = LogMessageWithCategory | string | Error;
type LogData = any;

const formatMessage = (message: LogMessage) => {
    const messageWithCategory = message as LogMessageWithCategory;
    if (messageWithCategory?.category) {
        return `[${messageWithCategory.category}] ${messageWithCategory.message}`;
    }
    return message;
};

const logToConsole = (
    level: LogLevel,
    message: LogMessage,
    data?: LogData
) => {
    if (canLog(level)) {
        const method = LOG_LEVEL_METHOD[level];
        if (method) {
            // error and warn includes trace by default
            const logTrace = (window as any).__logTrace && ![LogLevel.Error, LogLevel.Warn].includes(level);
            const logData = [getDatePrefix(), formatMessage(message)];
            if (logTrace) {
                console.groupCollapsed(logData.join(' '));
            }
            if (data) {
                logData.push(data);
            }
            console[method](...logData);
            if (logTrace) {
                console.trace(); // hidden in collapsed group
                console.groupEnd();
            }
        } else {
            console.error('Unsupported log level:', level);
        }
    }
};

export const logger = {
    debug: (message: LogMessage, data?: LogData) => {
        logToConsole(LogLevel.Debug, message, data);
    },
    info: (message: LogMessage, data?: LogData) => {
        logToConsole(LogLevel.Info, message, data);
    },
    warn: (message: LogMessage, data?: LogData) => {
        logToConsole(LogLevel.Warn, message, data);
    },
    error: (message: LogMessage, data?: LogData) => {
        logToConsole(LogLevel.Error, message, data);
    },
    category: LogCategory
};

// To handle logs from kurento-utils/lib/WebRtcPeer.js
(window as any).Logger = logger;
